// In-memory dedupe of AI analysis failures so a 500 from the upstream model
// provider does not produce one console error per ad on every page mount.
// This is a Phase 1 stop-gap; Phase 2 layers a Supabase-backed cache that
// also short-circuits successful analyses.

const TRANSIENT_TTL_MS = 15 * 60 * 1000;
const PERMANENT_TTL_MS = 60 * 60 * 1000;

const failures = new Map();

const now = () => Date.now();

const isTransient = (error) => {
  const status = Number(error?.status || 0);
  if (status === 408 || status === 425 || status === 429) return true;
  if (status >= 500 && status < 600) return true;
  const message = String(error?.message || "").toLowerCase();
  if (message.includes("failed to fetch")) return true;
  if (message.includes("network")) return true;
  if (message.includes("timed out") || message.includes("timeout")) return true;
  return false;
};

export const buildAnalyzeKey = (file, model) => {
  const name = file?.name || "anon";
  const size = Number(file?.size || 0);
  const stamp = Number(file?.lastModified || 0);
  const m = model || "default";
  return `${m}::${name}::${size}::${stamp}`;
};

export const recordAnalyzeFailure = (key, error) => {
  if (!key) return;
  const transient = isTransient(error);
  const ttl = transient ? TRANSIENT_TTL_MS : PERMANENT_TTL_MS;
  failures.set(key, {
    message: String(error?.message || "AI request failed"),
    status: Number(error?.status || 0) || null,
    transient,
    failedAt: now(),
    expiresAt: now() + ttl,
  });
};

export const getAnalyzeFailure = (key) => {
  if (!key) return null;
  const entry = failures.get(key);
  if (!entry) return null;
  if (entry.expiresAt && entry.expiresAt < now()) {
    failures.delete(key);
    return null;
  }
  return entry;
};

export const clearAnalyzeFailure = (key) => {
  if (!key) return;
  failures.delete(key);
};

export const clearAnalyzeFailures = () => {
  failures.clear();
};

export class CachedAnalyzeFailureError extends Error {
  constructor(entry) {
    super(entry?.message || "AI request failed");
    this.name = "CachedAnalyzeFailureError";
    this.status = entry?.status || null;
    this.transient = Boolean(entry?.transient);
    this.failedAt = entry?.failedAt || null;
    this.cached = true;
  }
}
